import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NaturezaMovimentacaoEnum } from '@shared/enums/financas/natureza-movimentacao.enum';
import { SituacaoMovimentacaoEnum } from '@shared/enums/financas/situacao-movimentacao.enum';
import { StatusContasReceberEnum } from '@shared/enums/financas/status-contas-receber.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { ContasReceberNFeSaidaLancadaStruct } from '@shared/services/structs/financas/contas-receber-nfe-saida-lancada.struct';
import { ContasReceberNFeSaidaParcelaStruct } from '@shared/services/structs/financas/contas-receber-nfe-saida-parcela.struct';
import { MovimentacaoNFeSaidaLancadaStruct } from '@shared/services/structs/financas/movimentacao-nfe-saida-lancada.struct';
import { UtilService } from '@shared/services/util.service';
import { DatetimeEmpresaPipe } from '@shared/pipes/datetime-empresa.pipe';
import { DiaCivilPipe } from '@shared/pipes/dia-civil.pipe';

const SituacaoParcela: Record<StatusContasReceberEnum, string> = {
    [StatusContasReceberEnum.EmAberto]: 'Em aberto',
    [StatusContasReceberEnum.Recebido]: 'Recebida',
    [StatusContasReceberEnum.Cancelada]: 'Cancelada',
    [StatusContasReceberEnum.RecebidoParcialmente]: 'Recebida parcialmente',
    [StatusContasReceberEnum.Perda]: 'Perda',
    [StatusContasReceberEnum.PerdaParcial]: 'Perda parcial',
};

/**
 * Títulos (com parcelas) e movimentações que o Finanças tem da NF-e de saída (cópia de `pv-financeiro-lancamentos`, fluxo
 * irmão da venda). Na consulta mostra a situação de cada item e o link para o registro no Finanças (outra aba, só para
 * quem pode ver a tela de destino); na exclusão, o que vai acontecer com cada item ou o motivo que impede.
 */
@Component({
    selector: 'app-nfe-financeiro-lancamentos',
    standalone: true,
    imports: [DiaCivilPipe, DatetimeEmpresaPipe, CommonModule, MatIconModule, MatTooltipModule, AbntPipe],
    templateUrl: './nfe-financeiro-lancamentos.component.html',
    styleUrls: ['./nfe-financeiro-lancamentos.component.css'],
})
export class NfeFinanceiroLancamentosComponent {
    @Input() contas: ContasReceberNFeSaidaLancadaStruct[] = [];
    @Input() movimentacoes: MovimentacaoNFeSaidaLancadaStruct[] = [];
    /** true: diálogo de excluir (ação prevista ou bloqueio por item); false: consulta (situação e link). */
    @Input() isExclusao = false;

    /** Link do título só para quem vê o contas a receber; da movimentação, só para quem vê o extrato. */
    public readonly podeVerContasReceber: boolean;
    public readonly podeVerExtrato: boolean;

    constructor(authService: AuthService, private router: Router) {
        const permissoes = authService.getUserInfos()?.listaPermissoes ?? [];
        this.podeVerContasReceber = permissoes.includes(PermissaoEnum.Financas_ContasReceber_Visualizar);
        this.podeVerExtrato = permissoes.includes(PermissaoEnum.Financas_ExtratoMovimentacoes_Visualizar);
    }

    /** Mesmas rotas de edição da lista do contas a receber (parâmetro de matriz) e do extrato, abertas em outra aba. */
    public linkTitulo(conta: ContasReceberNFeSaidaLancadaStruct): string {
        return this.router.serializeUrl(this.router.createUrlTree(['/financas/contas-receber/cadastrar', { idContasReceber: conta.idContasReceber }]));
    }

    public linkMovimentacao(mov: MovimentacaoNFeSaidaLancadaStruct): string {
        return this.router.serializeUrl(this.router.createUrlTree(['/financas/lancamentos/editar', mov.idMovimentacao]));
    }

    public isCancelada(parcela: ContasReceberNFeSaidaParcelaStruct): boolean {
        return parcela.idStatusContasReceber === StatusContasReceberEnum.Cancelada;
    }

    /** Na exclusão: o que acontece com a parcela. Cobrança (boleto/Pix) nunca é apagada: a parcela é cancelada com ela. */
    public acaoParcela(parcela: ContasReceberNFeSaidaParcelaStruct): string {
        if (parcela.motivoBloqueioExclusao) return parcela.motivoBloqueioExclusao;
        if (this.isCancelada(parcela)) return 'Já cancelada (agrupada no Finanças) - não muda';
        if (parcela.temCobranca) return 'Cancelar cobrança e parcela';
        return 'Excluir';
    }

    /** Na consulta: a situação da parcela no Finanças (a cancelada que continua contando é a agrupada). */
    public situacaoParcela(parcela: ContasReceberNFeSaidaParcelaStruct): string {
        if (this.isCancelada(parcela) && parcela.motivoBloqueioExclusao) return 'Agrupada';
        const situacao = SituacaoParcela[parcela.idStatusContasReceber as StatusContasReceberEnum] ?? 'Situação não informada';
        return parcela.temCobranca ? `${situacao} · com cobrança` : situacao;
    }

    public acaoMovimentacao(mov: MovimentacaoNFeSaidaLancadaStruct): string {
        return mov.motivoBloqueioExclusao ?? (this.isTarifa(mov) ? 'Excluir a tarifa e devolver o saldo à conta' : 'Excluir e desfazer o saldo da conta');
    }

    /** Saída com id_nfe_saida é a tarifa do intermediador; entrada é recebimento em conta. */
    public isTarifa(mov: MovimentacaoNFeSaidaLancadaStruct): boolean {
        return mov.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Saida;
    }

    /** Previstos no lançamento: taxa e tarifa do intermediador e juros da condição; vazio quando não há nenhum. */
    public previstos(parcela: ContasReceberNFeSaidaParcelaStruct): { rotulo: string; valor: number }[] {
        const partes: { rotulo: string; valor: number }[] = [];
        const taxa = parcela.valorTaxaIntermediadorPrevisto ?? 0;
        const tarifa = parcela.valorTarifaIntermediadorPrevisto ?? 0;
        const juros = parcela.valorJurosPrevisto ?? 0;

        if (taxa > 0) partes.push({ rotulo: 'taxa', valor: taxa });
        if (tarifa > 0) partes.push({ rotulo: 'tarifa', valor: tarifa });
        if (taxa > 0 || tarifa > 0) partes.push({ rotulo: 'líquido', valor: UtilService.arredondarAbnt(parcela.valor - taxa - tarifa, 2) });
        if (juros > 0) partes.push({ rotulo: 'juros', valor: juros });

        return partes;
    }

    public situacaoMovimentacao(mov: MovimentacaoNFeSaidaLancadaStruct): string {
        return mov.idSituacaoMovimentacao === SituacaoMovimentacaoEnum.Conciliado ? 'Conciliada' : 'Não conciliada';
    }
}
